import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LIVE_LIST_OPTIONS } from '../../utils/liveList';
import { useNavigate } from 'react-router-dom';
import { patientService } from '../../services/api';
import { Patient, PaginatedResponse } from '../../types';
import { Search, Plus, Edit, Eye, Phone, Mail, User, Calendar, FileText, Clock, Trash2, Shield } from 'lucide-react';
import PatientForm from './PatientForm';
import PatientDetail from './PatientDetail';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole, hasAnyRole } from '../../utils/roles';
import DoublonsPatients from './DoublonsPatients';

const PatientList: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Dossiers créés par les patients via WhatsApp, à compléter par l'accueil
  const [aVerifier, setAVerifier] = useState<boolean>(false);
  const [voirDoublons, setVoirDoublons] = useState<boolean>(false);
  const peutFusionner = hasAnyRole(user, ['admin', 'secretary']);
  const navigate = useNavigate();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset à la page 1 lors d'une nouvelle recherche
    }, 500); // Attendre 500ms après que l'utilisateur arrête de taper

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, refetch, error } = useQuery<PaginatedResponse<Patient>>({
    queryKey: ['patients', debouncedSearchTerm, currentPage, aVerifier],
    queryFn: () => patientService.getAll({
      search: debouncedSearchTerm,
      page: currentPage,
      ...(aVerifier ? { a_verifier: true } : {}),
    }).then(res => res.data),
    ...LIVE_LIST_OPTIONS,
  });

  const patients = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / 10);

  const getGenreLabel = (genre: 'M' | 'F'): string => {
    return genre === 'M' ? 'Masculin' : 'Féminin';
  };

  const handleEdit = (patient: Patient): void => {
    setSelectedPatient(patient);
    setShowForm(true);
  };

  const handleView = (patient: Patient): void => {
    setSelectedPatient(patient);
    setShowDetail(true);
  };

  const handleCloseForm = (): void => {
    setShowForm(false);
    setSelectedPatient(null);
  };

  const handleCloseDetail = (): void => {
    setShowDetail(false);
    setSelectedPatient(null);
  };

  const handleSuccess = (): void => {
    refetch();
    handleCloseForm();
  };

  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
  };

  const handleCreateConsultation = (patient: Patient): void => {
    navigate('/consultations', {
      state: {
        patientId: patient.id,
        patientInfo: {
          nom: patient.nom,
          prenoms: patient.prenoms,
          telephone: patient.telephone
        }
      }
    });
  };

  const handleScheduleAppointment = (patient: Patient): void => {
    navigate('/planning', {
      state: {
        selectedPatientId: patient.id,
        patientInfo: {
          nom: patient.nom,
          prenoms: patient.prenoms,
          telephone: patient.telephone
        }
      }
    });
  };

  const handleDelete = async (patient: Patient): Promise<void> => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le patient ${patient.nom} ${patient.prenoms} ?\n\nCette action est irréversible et supprimera toutes les données associées (consultations, rendez-vous, etc.).`)) {
      return;
    }

    try {
      await patientService.delete(patient.id);
      refetch();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du patient. Le patient a peut-être des données associées.');
    }
  };

  if (showForm) {
    return (
      <PatientForm
        patient={selectedPatient || undefined}
        onClose={handleCloseForm}
        onSuccess={handleSuccess}
      />
    );
  }

  if (showDetail && selectedPatient) {
    return (
      <PatientDetail
        patientId={selectedPatient.id}
        onClose={handleCloseDetail}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gestion des Patients</h1>
          <p className="text-gray-600 mt-1">{totalCount} patient(s) enregistré(s)</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn btn-primary shadow-colored-blue"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau Patient
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <input
          type="text"
          placeholder="Rechercher un patient (nom, prénom, téléphone...)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 transition-all"
        />
        {searchTerm !== debouncedSearchTerm && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>

      <div className="son-seg" role="group" aria-label="Filtrer les dossiers">
        {[
          { value: false, label: 'Tous' },
          { value: true, label: 'À vérifier' },
        ].map((opt) => (
          <button
            key={opt.label}
            type="button"
            aria-pressed={!voirDoublons && aVerifier === opt.value}
            onClick={() => { setAVerifier(opt.value); setVoirDoublons(false); setCurrentPage(1); }}
          >
            {opt.label}
          </button>
        ))}
        {peutFusionner && (
          <button type="button" aria-pressed={voirDoublons} onClick={() => setVoirDoublons(true)}>
            Doublons
          </button>
        )}
      </div>

      {voirDoublons ? <DoublonsPatients /> : (<>
      {/* Gestion des erreurs */}
      {Boolean(error) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-red-800">
              <strong>Erreur de chargement:</strong> Impossible de récupérer la liste des patients.
            </div>
          </div>
          <button 
            onClick={() => refetch()} 
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des patients...</p>
        </div>
      )}

      {/* Liste des patients — gardée si un rafraîchissement en arrière-plan échoue */}
      {!isLoading && data && (
        <>
          {patients.length === 0 ? (
            <div className="text-center py-12">
              <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun patient trouvé</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm ? 'Aucun patient ne correspond à votre recherche.' : 'Commencez par ajouter votre premier patient.'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="btn btn-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un patient
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Vue liste améliorée */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {patients.map((patient) => (
                  <div
                    key={patient.id}
                    className="son-panel hover:shadow-[var(--shadow-card-hover)] transition-shadow duration-200 overflow-hidden patient-card"
                  >
                    {/* Header de la carte */}
                    <div className={`p-4 ${patient.assureur_details ? 'patient-card-assurance' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="son-avatar !h-10 !w-10 !text-[13px]" aria-hidden>
                            {`${patient.nom?.[0] ?? ''}${patient.prenoms?.[0] ?? ''}`.toUpperCase()}
                          </span>
                          <div>
                            <h3 className="font-bold text-[color:var(--text)] text-[15px]">
                              {patient.nom} {patient.prenoms}
                              {patient.a_verifier && (
                                <span className="badge-yellow ml-2 align-middle" title="Dossier créé via WhatsApp, à compléter">
                                  À vérifier
                                </span>
                              )}
                            </h3>
                            <p className="text-[12.5px] text-[color:var(--muted)]">
                              {getGenreLabel(patient.genre)} • {patient.age} ans
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleView(patient)}
                            className="son-icon-btn is-blue"
                            title="Voir les détails"
                            aria-label="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(patient)}
                            className="son-icon-btn is-teal"
                            title="Modifier"
                            aria-label="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {hasRole(user, 'admin') && (
                            <button
                              onClick={() => handleDelete(patient)}
                              className="son-icon-btn is-red"
                              title="Supprimer"
                              aria-label="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Corps de la carte */}
                    <div className="p-4 space-y-3">
                      {/* Contact */}
                      <div className="space-y-2">
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="font-medium">{patient.telephone}</span>
                        </div>
                        {patient.email && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Mail className="h-4 w-4 mr-2 text-gray-400" />
                            <span className="truncate">{patient.email}</span>
                          </div>
                        )}
                      </div>

                      {/* Assurance */}
                      <div className="pt-2 border-t border-gray-100">
                        {patient.assureur_details ? (
                          <div className="rounded-xl p-3 bg-[color:var(--blue-soft)]">
                            <div className="flex items-center mb-1">
                              <Shield className="h-4 w-4 text-[#1565B8] mr-2" aria-hidden />
                              <span className="font-medium text-blue-800 text-sm">
                                {patient.assureur_details.nom}
                              </span>
                            </div>
                            <p className="text-xs text-blue-600">
                              N° {patient.numero_assure}
                            </p>
                            {patient.nom_souscripteur && (
                              <p className="text-xs text-blue-600 mt-1">
                                Souscripteur: {patient.nom_souscripteur}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-2">
                            <span className="text-xs text-gray-400">Non assuré</span>
                          </div>
                        )}
                      </div>

                      {/* Actions rapides */}
                      <div className="pt-3 border-t border-gray-100">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleCreateConsultation(patient)}
                            className="btn-secondary flex-1 !px-3 text-sm"
                            title="Créer consultation"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Consultation
                          </button>
                          <button
                            onClick={() => handleScheduleAppointment(patient)}
                            className="btn-primary flex-1 !px-3 text-sm"
                            title="Planifier RDV"
                          >
                            <Clock className="h-4 w-4 mr-1" />
                            RDV
                          </button>
                        </div>
                      </div>

                      {/* Date d'enregistrement */}
                      <div className="flex items-center text-xs text-gray-500 pt-2">
                        <Calendar className="h-3 w-3 mr-1" />
                        <span>
                          Enregistré le {new Date(patient.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg">
                  <div className="flex items-center text-sm text-gray-700">
                    <span>
                      Affichage de {((currentPage - 1) * 10) + 1} à {Math.min(currentPage * 10, totalCount)} sur {totalCount} patients
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 text-gray-700 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Précédent
                    </button>
                    
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 border rounded-md text-sm ${
                              currentPage === page
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 text-gray-700 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
      </>)}
    </div>
  );
};


export default PatientList;